/**
 * Module: observability/sentry
 * Role: Single init point for browser Sentry SDK (TECH-1172).
 *       DSN env-gated: empty VITE_SENTRY_DSN = no-op (dev default).
 *       Tags: platform from PlatformAdapter (ADR-023), client_version from build.
 *
 * Uses: @sentry/react, platform/types (PlatformAdapter)
 * Used by: main.tsx (once, before createRoot)
 */

import * as Sentry from '@sentry/react';
import type { PlatformAdapter } from '../platform/types';

// Version injected at build time via vite.config.ts define block
declare const __APP_VERSION__: string;

/**
 * Initialize Sentry for the miniapp.
 *
 * No-op when VITE_SENTRY_DSN is empty (default in dev).
 * Call once at module level before createRoot.
 * Platform tags are set separately via setSentryPlatformTags() after
 * PlatformAdapter resolves.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return; // env-gated: dev/test = silent
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // 'production' | 'development'
    release: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
    sendDefaultPii: false, // Privacy: no IP/cookies/headers (Phase A)
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Error sampling: 100% (low volume miniapp), traces at 10%
    sampleRate: 1.0,
    tracesSampleRate: 0.1,
  });

  // Version tag available without adapter
  Sentry.setTag('client_version',
    typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
  );
}

/**
 * Set platform-specific Sentry tags after PlatformAdapter resolves.
 *
 * Reads apiPlatform from adapter (ADR-023: never read window.Telegram directly).
 * No-op when VITE_SENTRY_DSN is empty.
 *
 * @param adapter - Resolved PlatformAdapter from PlatformProvider
 */
export function setSentryPlatformTags(adapter: PlatformAdapter): void {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    return;
  }
  Sentry.setTag('platform', adapter.apiPlatform ?? 'unknown');
}

/** Re-export ErrorBoundary for use in main.tsx */
export { ErrorBoundary } from '@sentry/react';
